import { useState, useEffect, useContext } from 'react';
import { IS_STANDALONE } from '../../constants';
import { useAudio } from '../../providers/AudioContext';
import { safeGtagEvent } from '../../providers/AnalyticsProvider';
import { leaveGameSession } from '../../providers/BasejumpApiProvider';
import {
  StyledNavBar,
  NavBarStatsContainer,
  NavBarStat,
  NavBarStatIcon,
  NavBarStatValue,
  NavBarPointsContainer,
  NavBarPointsRow,
  NavBarPointsLabel,
  NavBarPointsValue,
  StandaloneAccountInfoHolder,
  StandaloneAccountInfoAddress,
  StandaloneAccountInfoImage,
  StyledVolumeButton,
  VolumeControls,
  BasejumpNavLogo
} from './NavBar.styled';
import {
  ConfirmPopup,
  SessionResultPopup,
  PointsPopup,
  MapModal,
  LogoutModal,
  UploadModal,
  BroadsiderInfo,
  PlainButton,
  SelectableButton,
  AppMenuModal,
  BroadsiderSelectorModal,
  AssetModal,
  ShopAssetModal,
  QrCodePopup,
  ErrorPopup,
  LoginModal,
  DesktopOnlyModal,
  VideoModal,
  SyncModal,
  TransferModal,
  TutorialModal,
  MintCompleteModal,
  BattleNowPopup
} from '../../components';
import { useWeb3Modal } from '@web3modal/wagmi/react'
import { AppContext } from '../../providers/AppContext';
import { openUrl } from '../../utils';
import {
  useNavigate,
  useLocation
} from "react-router-dom";
import { useAccountEffect } from 'wagmi';
import { theme } from '../../theme';
import useIsMobile from '../../hooks/useIsMobile';
import { MintModal } from '../MintActionFigures';
import { useTokenAuth } from '../../hooks';
import { useTutorialStore } from '../../store/tutorialModal.store';
import { useEarningsStore } from '../../store/gameEarnings.store';
import useAppEvent from '../../hooks/useAppEvent';
import { NavIndex } from '../../enums/navigationEnum';
import EarningsWrapModal from '../EarningsWrapModal/EarningsWrapModal';
import { MISSIONS_ENABLED } from "../../constants";

const NO_AVATAR_IMG_SRC = '/assets/no_avatar.png';

const BasejumpGameLogo = () => {
  return (
    <BasejumpNavLogo src='/assets/bj_logo.svg'/>
  );
};

const AccountSection = ({onClick, address}) => {
  return (
    <div className='nav-account-info'>
      <div className='nav-account-info-address'>{address}</div>
      <SelectableButton
        selected={false}
        isIconButton={true}
        backgroundOverwrite={theme.buttonBrightBlue}
        borderOverwrite='rgba(0, 0, 0, .16)'
        onClick={onClick}
        img='/assets/account_black.svg' />
    </div>
  );
};

const StandaloneAccountInfo = ({ children }) => {
  return (
    <StandaloneAccountInfoHolder>
      <StandaloneAccountInfoAddress>{children}</StandaloneAccountInfoAddress>
      <StandaloneAccountInfoImage>
        <img src='/assets/account_black.svg'/>
      </StandaloneAccountInfoImage>
    </StandaloneAccountInfoHolder>
  );
};

const VolumeButton = ({ onClick, assetOn, assetOff, on }) => {
  const { playSFX } = useAudio();
  const onVolumeClick = () => {
    playSFX('button');
    if (onClick) {
      onClick();
    }
  };

  return (
    <StyledVolumeButton onMouseEnter={() => playSFX('hover')} onClick={onVolumeClick} src={on ? assetOn : assetOff} />
  )
}

const NavBarStats = ({ avatarStats, avatarStatsModifiers }) => {
  const healthDecreased = Math.floor(avatarStats.healthPoints * avatarStatsModifiers.healthMultiplier + avatarStatsModifiers.healthPoints);
  let healthLabel = `${avatarStats.healthPoints}`;
  if (healthDecreased !== avatarStats.healthPoints) {
    healthLabel = `${healthDecreased}/${avatarStats.healthPoints}`;
  }
  return (
    <NavBarStatsContainer>
      <NavBarStat $reduced={healthDecreased !== avatarStats.healthPoints} data-tutorial-anchor="navbar-stat-1">
        <NavBarStatIcon src='/assets/nb_icon_health.svg'/>
        <NavBarStatValue>{healthLabel}</NavBarStatValue>
      </NavBarStat>
      <NavBarStat data-tutorial-anchor="navbar-stat-2">
        <NavBarStatIcon src='/assets/nb_icon_weapon.svg'/>
        <NavBarStatValue>{avatarStats.attackPoints}</NavBarStatValue>
      </NavBarStat>
      <NavBarStat data-tutorial-anchor="navbar-stat-3">
        <NavBarStatIcon src='/assets/nb_icon_armour.svg'/>
        <NavBarStatValue>{avatarStats.defencePoints}</NavBarStatValue>
      </NavBarStat>
    </NavBarStatsContainer>
  );
};

const NavBarPoints = ({ user }) => {
  const { playSFX } = useAudio();
  const { setShowPointsPopup } = useContext(AppContext);
  return (
    <NavBarPointsContainer onClick={() => { playSFX('button'); setShowPointsPopup(true); }} onMouseEnter={() => playSFX('hover')}>
      <NavBarPointsRow>
        <NavBarPointsLabel><img src='/assets/coin_low.gif'/><div>BONES:</div></NavBarPointsLabel>
        <NavBarPointsValue>{user?.points || 0}</NavBarPointsValue>
      </NavBarPointsRow>
      <NavBarPointsRow>
        <NavBarPointsLabel><img src='/assets/points.svg'/><div>XP:</div></NavBarPointsLabel>
        <NavBarPointsValue>{user?.xp || 0}</NavBarPointsValue>
      </NavBarPointsRow>
    </NavBarPointsContainer>
  );
}

const MAX_USERNAME_LENGTH = 12;
const handleLongUsername = (username) => {
  if (username && username.length > MAX_USERNAME_LENGTH) {
    return `${username.substring(0, MAX_USERNAME_LENGTH - 1)}...`;
  }
  return username;
};

const NavBar = ({ navOpen: open, setNavOpen: setOpen, isTransparent = false }) => {
  const isMobile = useIsMobile();
  const { open: openWalletPopup } = useWeb3Modal();
  const {
    transferAsset,
    setTransferAsset,
    selectedBroadsider,
    selectedAsset,
    setAccountAddress,
    setWalletAddress,
    setSelectedAsset,
    selectedShopAsset,
    setSelectedShopAsset,
    qrCodeData,
    setQrCodeData,
    setErrorData,
    errorData,
    setConfirmData,
    confirmData,
    showLogin,
    setShowLogin,
    accountAddress,
    moonpaySdk,
    showLogout,
    setShowLogout,
    setShowDesktopOnly,
    showDesktopOnly,
    setShowVideo,
    showVideo,
    showMintModal,
    setShowMintModal,
    mapSrc,
    setMapSrc,
    completeReceipt,
    setCompleteReceipt,
    showUpload,
    setShowUpload,
    showSelector,
    setShowSelector,
    avatarStats,
    avatarStatsModifiers,
    syncResults,
    setSyncResults,
    showPointsPopup,
    setShowPointsPopup,
    gameSessionData,
    setGameSessionData,
    readySessionId,
    loadedSessionId
  } = useContext(AppContext);
  const navigate = useNavigate();
  const myLocation = useLocation();
  useAccountEffect({
    onConnect({ address }) {
      setWalletAddress(address);
    },
    onDisconnect() {
      setWalletAddress(null);
    }
  });
  
  //const { disconnect } = useDisconnect();

  const {
    earnings,
    popEarning,
    gameSessionResult,
    showAcceptModal,
    showBattleNowModal,
    hideBattleNowModal,
    closeAcceptModal,
    lastSessionResult

  } = useEarningsStore();
  const showEarningsModal = earnings?.length && !showAcceptModal;
  const { dispatchAppEvent } = useAppEvent('modalAction');
  const { hideTutorial, showTutorialModal } = useTutorialStore();

  const { user, basejumpToken } = useTokenAuth();

  useEffect(() => {
    if (user) {
      setAccountAddress(user.user_name);
      if (
        myLocation.pathname !== "/impact" &&
        myLocation.pathname !== "/missions" &&
        !IS_STANDALONE
      ) {
        navigate("/app");
      }
    } else {
      setAccountAddress(null);
    }
  }, [user]);

  const onCancelSession = async () => {
    await leaveGameSession(gameSessionData.id, basejumpToken);
    setGameSessionData(null);
  };

  const showAvatarSelector = myLocation.pathname === '/app' || IS_STANDALONE;
  const showMenuButton = !IS_STANDALONE;
  const showAddressButton = !IS_STANDALONE;
  const appMenuOptions = [
    {
      name: "Home",
      onSelect: () => {
        navigate("/home");
        if (isMobile) {
          setShowMenu(false);
        }
      },
      url: "/home",
    },
    // { name: 'About', onSelect: () => {
    //   navigate('/broadside');
    //   if (isMobile) {
    //     setShowMenu(false);
    //   }
    // }, url: '/broadside' },
    {
      name: "App",
      onSelect: () => {
        navigate("/app");
        if (isMobile) {
          setShowMenu(false);
        }
      },
      disabled: !accountAddress || isMobile,
      url: "/app",
    },
    {
      name: "Impact",
      onSelect: () => {
        navigate("/impact");
        if (isMobile) {
          setShowMenu(false);
        }
      },
      url: "/impact",
    },
  ];

  if (MISSIONS_ENABLED) {
    appMenuOptions.push({
      name: "Missions",
      onSelect: () => {
        navigate("/missions");
        if (isMobile) {
          setShowMenu(false);
        }
      },
      url: "/missions",
    });
  }

  const appMenuSubsections = [
    { name: 'Community', items: [
      { name: 'Whitepaper', onSelect: () => openUrl('/action') },
      { name: 'Twitter', onSelect: () => openUrl('https://twitter.com/basejumpxyz') },
      { name: 'Discord', onSelect: () => openUrl('http://discord.gg/basejump') }
    ] },
    { name: 'Legal', items: [
      { name: 'Terms of Service', onSelect: () => openUrl('/terms') },
      { name: 'Privacy Policy', onSelect: () => openUrl('/privacy') }
    ] }
  ];

  const onClickConnect = async () => {
    console.error({accountAddress});
    if (accountAddress) {
      if (showAvatarSelector) {
        setShowLogout(!showLogout);
      } else {
        navigate('/app');
      }
    } else {
      safeGtagEvent('click_login', {});
      setShowLogin(true);
    }
  };

  const hideAllPopups = () => {
    setShowSelector(false);
    setShowMenu(false);
    setSelectedAsset(null);
    setSelectedShopAsset(null);
    setTransferAsset(null);
    setQrCodeData(null);
    setErrorData(null);
    setConfirmData(null);
    setShowLogin(false);
    setShowDesktopOnly(false);
    setShowLogout(false);
    setShowVideo(false);
    setShowMintModal(false);
    setCompleteReceipt(null);
    setShowUpload(false);
    setShowPointsPopup(false);
    setMapSrc(null);
    setSyncResults(null);
    hideBattleNowModal();
  };

  const onCloseEarningsModal = () => {
    popEarning();
    // Last earning modal closed
    if (earnings.length === 1 && lastSessionResult) {
      dispatchAppEvent({
        type: 'postBattleDone',
      });
    }
  }

  const onAcceptResult = () => {
    // Set accepted
    closeAcceptModal();
    dispatchAppEvent({
      type: 'navigate',
      data: NavIndex.Kitchen
    });

    // If there are no earnings to show
    // post battle view is finished
    if (!earnings.length) {
      dispatchAppEvent({
        type: 'postBattleDone',
      });
    }
  };

  const onBattleNow = () => {
    hideBattleNowModal();
    dispatchAppEvent({
      type: 'battle',
    });
  }

  const onErrorPopupClose = ({ closeEvent: { type = null, data = null } = { type: null, data: null } }) => {
    setErrorData(null);
    if (type) {
      dispatchAppEvent({type, data});
    }
  }
  
  const { musicVolume, sfxVolume, setMusicVolume, setSFXVolume, playSFX } = useAudio();
  const [showMenu, setShowMenu] = useState(false);
  const showBroadsiderInfo = accountAddress && selectedBroadsider && showAvatarSelector;
  const avatarImgSrc = selectedBroadsider ? selectedBroadsider.getAvatarImgSource() : NO_AVATAR_IMG_SRC;
  const addrLabel = accountAddress; //? composeShortAddress(accountAddress) : '';
  const selectedMenuIndex = appMenuOptions.findIndex((o) => o.url === myLocation.pathname) || 0;

  const musicOn = musicVolume > 0;
  const sfxOn = sfxVolume > 0;

  const muteMusic = (mute = false) => {
    setMusicVolume(mute ? 0 : 0.5);
  };

  const muteSfx = (mute = false) => {
    setSFXVolume(mute ? 0 : 1.0);
  };
	
  const onSelect = (index) => {
    const selectedOption = appMenuOptions[index];
    selectedOption.onSelect();
  };

  const onClickQuit = () => {
    setConfirmData({ title: 'Quit Game', description: 'Are you sure you want to quit?' });
  };
  return (
    <StyledNavBar $isTransparent={isTransparent}>
      <BasejumpGameLogo/>
      <div className='nav-info-and-controls'>
        {gameSessionData && <PlainButton disabled={readySessionId && loadedSessionId !== readySessionId} onClick={() => onClickQuit()}>Quit Game</PlainButton>}
        {showBroadsiderInfo && <VolumeControls>
          <VolumeButton on={musicOn} onClick={() => muteMusic(musicOn)} assetOn='/assets/music_on.svg' assetOff='/assets/music_off.svg'/>
          <VolumeButton on={sfxOn} onClick={() => muteSfx(sfxOn)} assetOn='/assets/sfx_on.svg' assetOff='/assets/sfx_off.svg'/>
        </VolumeControls>}
        {showBroadsiderInfo && <NavBarPoints user={user}/>}
        {showBroadsiderInfo && <BroadsiderInfo hideType={true} broadsider={selectedBroadsider}/>}
        {showBroadsiderInfo && <NavBarStats avatarStats={avatarStats} avatarStatsModifiers={avatarStatsModifiers}/>}
        <div className={`nav-controls ${IS_STANDALONE ? 'nav-controls-standalone' : ''}`}>
          {
            !isMobile && accountAddress && showAvatarSelector 
            ? (<SelectableButton selected={showSelector} img={avatarImgSrc} onClick={() => setShowSelector(!showSelector)}/>) 
            : <div />
          }
	  {!isMobile && accountAddress && !showAvatarSelector
            ? (<AccountSection onClick={() => setShowLogout(!showLogout)} address={accountAddress}/>)
            : (IS_STANDALONE ? <StandaloneAccountInfo>{handleLongUsername(accountAddress)}</StandaloneAccountInfo> : <div />)}
	  {!isMobile && showAddressButton && <PlainButton onClick={onClickConnect}>{accountAddress ? (showAvatarSelector ? `${addrLabel}` : <div className='nav-open-app'><div>Open App</div><img src='/assets/controller.svg' /></div>) : 'Login'}</PlainButton>}
	  {showMenuButton && <SelectableButton selected={showMenu} isIconButton={true} onClick={() => setShowMenu(!showMenu)} imgHover={showMenu ? '/assets/close_hover.svg' : null} img={showMenu ? '/assets/close.svg' : '/assets/menu.svg'} />}
        </div>
      </div>
      <div className='app-popup-background' style={{ visibility: !!showBattleNowModal || !!showAcceptModal || !!showTutorialModal || !!mapSrc || !!completeReceipt || showMintModal || showVideo || showMenu || showSelector || !!selectedAsset || !!selectedShopAsset || !!transferAsset || !!qrCodeData  || !!confirmData || !!errorData || !!showLogin || !!showLogout || !!showUpload || !!showDesktopOnly || earnings.length > 0 || syncResults || showPointsPopup ? 'visible' : 'hidden' }} onClick={hideAllPopups}>
        {transferAsset && <TransferModal asset={transferAsset} />}
        {selectedAsset && <AssetModal asset={selectedAsset}/>}
        {selectedShopAsset && <ShopAssetModal asset={selectedShopAsset}/>}
        {!!showEarningsModal && <EarningsWrapModal earnings={earnings[0]} onClose={onCloseEarningsModal} />}
        {syncResults && <SyncModal syncResults={syncResults}/>}
        {qrCodeData && <QrCodePopup data={qrCodeData} />}
        {errorData && <ErrorPopup data={errorData} onClose={() => onErrorPopupClose(errorData)}/>}
        {confirmData && <ConfirmPopup data={confirmData} onClose={() => setConfirmData(null)} onConfirm={() => { onCancelSession(); setConfirmData(null); }}/>}
        {showLogin && <LoginModal/>}
        {showPointsPopup && <PointsPopup/>}
        {showUpload && <UploadModal/>}
        {showDesktopOnly && <DesktopOnlyModal/>}
        {showVideo && <VideoModal/>}
        {showMintModal && <MintModal />}
        {completeReceipt && <MintCompleteModal receipt={completeReceipt}/>}
        {showAcceptModal && <SessionResultPopup data={gameSessionResult} onAccept={onAcceptResult}/>}
        {mapSrc && <MapModal src={mapSrc} />}
        {!!showTutorialModal && <TutorialModal onClose={() => hideTutorial()} tutorialId={showTutorialModal} />}
        {showBattleNowModal && <BattleNowPopup onClose={onBattleNow}/>}
      </div>
      {showLogout && <LogoutModal/>}
      {showSelector && <BroadsiderSelectorModal />}
      {showMenu && <AppMenuModal options={appMenuOptions} subsections={appMenuSubsections} onSelect={onSelect} selectedIndex={selectedMenuIndex}/>}
    </StyledNavBar>
  )
};

export default NavBar;

